import { type ReactNode, useState } from 'react';

import { useTemporaryImperativeFocus } from '../../../common/hooks/useTemporaryImperativeFocus';
import SkipButton from './SkipButton';
import SkipLinkLayout from './SkipLinkLayout';

/**
 * Provides a way to skip over a group of elements for A11y purposes.
 * It creates a SkipLink component at the beginning of a group and a focusable div at it's end.
 * When SkipLink is triggered, focus is moved to the focusable div.
 * The user can then go to next focusable item after the group.
 */
const SkipLinkScoped = ({
    align = 'start',
    skipLinkText,
    'aria-describedby': ariaDescribedBy,
    children,
    skipTargetText,
}: {
    align?: 'start' | 'center';
    skipTargetText?: string;
    'aria-describedby'?: string;
    skipLinkText: string;
    children: ReactNode;
}) => {
    // to avoid SR to be able to focus on the visually hidden target text
    // we only show text when the SkipLink is clicked and remove on blur
    // That ensures the the target container stays empty when not in use
    const [targetTextVisible, setTargetTextVisible] = useState(false);
    const { ref, onBlurCapture, moveFocus } = useTemporaryImperativeFocus<HTMLDivElement>();

    const onClick = () => {
        setTargetTextVisible(true);
        moveFocus();
    };
    const onBlur = (e: React.FocusEvent<HTMLDivElement>) => {
        setTargetTextVisible(false);
        onBlurCapture(e);
    };

    return (
        <SkipLinkLayout
            skipTriggerSlot={
                <SkipButton align={align} onClick={onClick} aria-describedby={ariaDescribedBy}>
                    {skipLinkText}
                </SkipButton>
            }
            endSlot={
                <div className="u-hiddenVisually" ref={ref} onBlurCapture={onBlur}>
                    {targetTextVisible ? skipTargetText : null}
                </div>
            }
        >
            {children}
        </SkipLinkLayout>
    );
};

export default SkipLinkScoped;
